<script lang="ts">
  import {
    applyReducer,
    getFrameStyle,
    styleToString,
  } from "@shared/common/node/style";
  import type {
    IFrameNode,
    IMediaPaint,
    PartialNode,
  } from "@shared/common/node/type";
  import { getAspectRatioStyle } from "@shared/common/node/util";
  import type { VideoInfo } from "@shared/common/types/data-source";
  import { getDataSourceValue } from "@shared/common/utils/data-source";
  import { assertCondition, isObject } from "@shared/common/utils/value";
  import detectReaction from "../../actions/detect-reaction";
  import { getCurrentDocumentNode, getStore } from "../../context";
  import BackgroundVideo from "./background-video.svelte";
  import FrameWithEntranceImage from "./frame-with-entrance-image.svelte";

  const { captureMode, reactionEventListener, dataMap } = getStore();
  const documentNode = getCurrentDocumentNode();

  export let node: PartialNode<IFrameNode>;
  // 캡처시 가장 최상단 프레임의 그림자를 제거
  export let hideShadow: boolean;

  let entranceImageEnabled =
    !$captureMode &&
    node.entranceImage?.active &&
    node.entranceImage?.image?.path;

  let image: IMediaPaint | undefined = node.fills?.find(
    (fill): fill is IMediaPaint => fill.type === "IMAGE"
  );
  let isBackgroundVideo =
    node.fills && image?.visible && image?.video?.fileName;

  const videoInfoAssertion = assertCondition<VideoInfo[]>(
    (val): val is VideoInfo[] =>
      Array.isArray(val) &&
      val.every(
        (item) => isObject(item) && "status" in item && "videoUrl" in item
      )
  );

  $: videoSrc = videoInfoAssertion
    .typeSafeValue(
      getDataSourceValue({
        dataMap: $dataMap ?? {},
        keyString: "video",
      }),
      []
    )
    .find((video) => {
      if (image?.type === "IMAGE" && image.video?.fileName === video.filename) {
        return true;
      }
    });
</script>

{#if entranceImageEnabled}
  <!-- 이미지 진입 애니메이션 있는 프레임  -->
  <FrameWithEntranceImage {node} {hideShadow}>
    <slot />
  </FrameWithEntranceImage>
{:else}
  <!-- 일반적인 프레임 -->
  <div
    id={node.id ? `node-${node.id}` : undefined}
    use:detectReaction={{
      node,
      documentNode,
      onAction: reactionEventListener,
    }}
    class:child-shrink-none={!!node.overflow && node.overflow !== "NONE"}
    style={styleToString(
      applyReducer(getFrameStyle(node), (style) => {
        // background로 비디오가 있을 경우, 비디오의 해상도로 aspectRatio를 조정
        if (isBackgroundVideo) {
          if (
            node.aspectRatio &&
            node.aspectRatio.type === "BACKGROUND_IMAGE"
          ) {
            style.aspectRatio = getAspectRatioStyle({
              type: node.aspectRatio.type,
              imageHeight: videoSrc?.height,
              imageWidth: videoSrc?.width,
            });
          }

          style.background = "none";
        }

        if (hideShadow) {
          const { boxShadow: _boxShadow, ...last } = style;
          return last;
        }
        return style;
      })
    )}
  >
    {#if isBackgroundVideo}
      <BackgroundVideo fills={node.fills} {videoSrc} />
    {/if}
    <slot />
  </div>
{/if}

<style>
  .child-shrink-none > :global(*) {
    flex-shrink: 0;
  }
</style>
